import { Button, message, Select, Tabs } from "antd";
import type { QueryLicenseResponse, VehicleHistoryVo, VehicleInfoResponse } from "@zhihaoscm/entity";
import type { VehicleQueryLimitInfo } from "@/api/shipService";
import { VehicleQueryUsageCard } from "./FindLocationComponents";
import { VEHICLE_PLATE_COLOR } from "@zhihaoscm/meta";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { DateRangePicker } from "zhihao-ui-react";

const EMPTY_VALUE = "--";

/** 查车历史按 Vue searchHistory.vue 的 40px 行和独立删除图标渲染。 */
export function TruckSearchHistory({ history, usage, showUsage, onClear, onDelete, onSelect, onToggleUsage, onUpgrade }: {
    history: VehicleHistoryVo[];
    usage: VehicleQueryLimitInfo | null;
    showUsage: boolean;
    onClear: () => void;
    onDelete: (item: VehicleHistoryVo) => void;
    onSelect: (plate: string) => void;
    onToggleUsage: () => void;
    onUpgrade: () => void;
}) {
    return <div className="truck-location-history">
        <div className="truck-history-head"><strong>历史搜索</strong><span><button type="button" onClick={onClear}>清空</button>{usage ? <button className="truck-usage-info" aria-label="查看今日查车用量" type="button" onClick={onToggleUsage}><i className="iconfont icon-tishi" /></button> : null}</span></div>
        <div className="truck-history-content">{history.length ? history.map(item => <div className="truck-history-item" key={item.vclN}>
            <button type="button" onClick={() => onSelect(item.vclN)}><strong>{item.vclN}</strong><span>车牌颜色 {VEHICLE_PLATE_COLOR.getIntroByCode(item.vclColor) || EMPTY_VALUE}</span></button>
            <button aria-label={`删除${item.vclN}`} type="button" onClick={() => onDelete(item)}><i className="iconfont icon-close" /></button>
        </div>) : <div className="truck-history-empty">没有搜索历史</div>}</div>
        {showUsage && usage ? <VehicleQueryUsageCard usage={usage} onUpgrade={onUpgrade} /> : null}
    </div>;
}

interface VehicleDetailProps {
    plate: string;
    archive: QueryLicenseResponse;
    position?: VehicleInfoResponse;
    onTrack: () => void;
    onTrajectory: () => void;
}

/** 将 Vue 车辆档案字段转换为固定 60/40 两列布局。 */
export function TruckSearchDetail({ plate, archive, position, onTrack, onTrajectory }: VehicleDetailProps) {
    const [expanded, setExpanded] = useState(true);
    const fields = [
        ["车辆识别代码", archive.vin], ["车牌颜色", VEHICLE_PLATE_COLOR.getIntroByCode(archive.vclColor)],
        ["类型", archive.vclTpNm], ["总质量", archive.vclTon ? `${archive.vclTon}kg` : EMPTY_VALUE],
        ["核定载质量", archive.ldTn ? `${archive.ldTn}kg` : EMPTY_VALUE], ["准牵引总质量", archive.vclTon ? `${archive.vclTon}kg` : EMPTY_VALUE],
        ["货箱内部尺寸长/宽/高", [archive.boxLng, archive.boxWdt, archive.boxHgt].filter(Boolean).join("/") ? `${[archive.boxLng, archive.boxWdt, archive.boxHgt].filter(Boolean).join("/")}mm` : EMPTY_VALUE]
    ];
    return <div className="truck-location-detail">
        <button className="truck-detail-collapse" type="button" onClick={() => setExpanded(value => !value)}>
            <span className={`truck-location-plate color-${Number(archive.vclColor)}`}><span className="text">{plate.slice(0, 2)} · {plate.slice(2)}</span></span>
            <i className={`iconfont icon-down${expanded ? " expanded" : ""}`} />
        </button>
        {expanded ? <>
            <div className="truck-detail-section">
                <div className="truck-location-detail-head"><strong>车辆档案</strong><span>
                    <button type="button" onClick={onTrack}><i className="iconfont icon-localize" />车辆追踪</button>
                    <button type="button" onClick={onTrajectory}><i className="iconfont icon-orbit" />查车轨迹</button>
                </span></div>
                <div className="truck-detail-grid">{fields.map(([label, value]) => <div className="truck-detail-item" key={label}><span>{label}:</span><strong>{value || EMPTY_VALUE}</strong></div>)}</div>
            </div>
            {position ? <div className="truck-detail-section truck-track-section">
                <div className="truck-location-detail-head"><strong>车辆追踪</strong></div>
                <div className="truck-detail-grid">
                    <div className="truck-detail-item"><span>上报时间:</span><strong>{position.firstVcl?.utc ? dayjs(Number(position.firstVcl.utc)).format("YYYY-MM-DD HH:mm:ss") : EMPTY_VALUE}</strong></div>
                    <div className="truck-detail-item"><span>车速:</span><strong>{position.firstVcl?.spd ? `${position.firstVcl.spd}km/h` : EMPTY_VALUE}</strong></div>
                    <div className="truck-detail-item"><span>当前状态:</span><strong>{position.firstVcl?.offlineState ? "离线" : "在线"}</strong></div>
                    <div className="truck-detail-item"><span>已离线时长:</span><strong>{position.firstVcl?.offlineTime ? `${position.firstVcl.offlineTime}分钟` : EMPTY_VALUE}</strong></div>
                    <div className="truck-detail-item wide"><span>当前位置:</span><strong>{position.firstVcl?.adr || EMPTY_VALUE}</strong></div>
                </div>
            </div> : null}
        </> : null}
    </div>;
}

export interface TruckTrajectoryEntry {
    key: string;
    plate: string;
    archive: QueryLicenseResponse;
}

interface TruckTrajectoryForm {
    range: [dayjs.Dayjs, dayjs.Dayjs];
    speed: number;
}

const TRAJECTORY_TAB_COLORS = ["#ff0000", "#00ff00", "#0079ff", "#ffa500", "#800080", "#008080", "#ffc0cb", "#800000", "#000080", "#808000"];

/** Vue 每辆车独立保存最近一天的时间范围及播放速度。 */
const createTrajectoryForm = (): TruckTrajectoryForm => ({
    range: [dayjs().subtract(1, "day").subtract(3, "second"), dayjs().subtract(3, "second")],
    speed: 1
});

/** 查车轨迹弹层复刻 Vue driveTrajectory.vue 的时间、速度与操作区。 */
export function TruckTrajectoryPanel({ entries, activeKey, trackVisible, onActive, onRemove, onClose, onMini, onQuery, onPlay, onCenter }: {
    entries: TruckTrajectoryEntry[];
    activeKey: string;
    trackVisible: boolean;
    onActive: (key: string) => void;
    onRemove: (key: string) => void;
    onClose: () => void;
    onMini: () => void;
    onQuery: (entry: TruckTrajectoryEntry, range: [dayjs.Dayjs, dayjs.Dayjs], speed: number) => void;
    onPlay: (entry: TruckTrajectoryEntry, speed: number) => void;
    onCenter: (entry: TruckTrajectoryEntry) => void;
}) {
    const [forms, setForms] = useState<Record<string, TruckTrajectoryForm>>(() => Object.fromEntries(entries.map(entry => [entry.key, createTrajectoryForm()])));
    const activeEntry = entries.find(entry => entry.key === activeKey) ?? entries[0];
    const activeForm = forms[activeEntry?.key] ?? createTrajectoryForm();
    /** 与 Vue 表单规则一致：结束不得早于开始，且单次轨迹范围最多 72 小时。 */
    const validateAndQuery = (entry = activeEntry, nextRange = activeForm.range, nextSpeed = activeForm.speed) => {
        if (!entry) return;
        if (nextRange[1].isBefore(nextRange[0])) { message.error("开始日期不能大于结束日期"); return; }
        if (nextRange[1].diff(nextRange[0], "hour", true) > 72) { message.error("时间范围不能超过 72 小时"); return; }
        onQuery(entry, nextRange, nextSpeed);
    };
    /** 新增或切换车辆 Tab 时恢复该车表单，并像 Vue trackTabClick 一样立即刷新轨迹。 */
    useEffect(() => {
        const entry = entries.find(item => item.key === activeKey);
        if (!entry) return;
        const form = forms[entry.key] ?? createTrajectoryForm();
        if (!forms[entry.key]) setForms(current => ({ ...current, [entry.key]: form }));
        validateAndQuery(entry, form.range, form.speed);
        // 查询只由活动车辆变化触发，避免父组件回调重建造成重复请求。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeKey]);
    const updateActiveForm = (next: Partial<TruckTrajectoryForm>) => {
        if (!activeEntry) return;
        setForms(current => ({ ...current, [activeEntry.key]: { ...(current[activeEntry.key] ?? activeForm), ...next } }));
    };
    const tabItems = entries.map((entry, index) => {
        // Element Plus 仅在多车时添加 closable 占位，活动线也随之覆盖关闭图标区域。
        const closable = entries.length > 1;
        return {
            key: entry.key,
            label: <span className={`truck-trajectory-tab-label${closable ? " is-closable" : ""}`} style={{ color: TRAJECTORY_TAB_COLORS[index % TRAJECTORY_TAB_COLORS.length] }}><span>{entry.plate}</span>{closable ? <button className="truck-trajectory-tab-close" aria-label={`关闭${entry.plate}轨迹`} type="button" onClick={event => { event.stopPropagation(); onRemove(entry.key); }}><i className="iconfont icon-close" /></button> : null}</span>
        };
    });
    return <div className="truck-trajectory-panel">
        <div className="truck-trajectory-head"><Tabs activeKey={activeEntry?.key} items={tabItems} onChange={onActive} /><span><button aria-label="最小化轨迹" type="button" onClick={onMini}><i className="iconfont icon-Frame14" /></button><button aria-label="关闭轨迹" type="button" onClick={onClose}><i className="iconfont icon-close" /></button></span></div>
        <div className="truck-trajectory-content">
            <div className="truck-trajectory-form">
                <DateRangePicker className="truck-trajectory-date-picker" type="datetimerange" size="default" clearable={false} rangeSeparator="至" startPlaceholder="开始时间" endPlaceholder="结束时间" value={activeForm.range} disabledDate={date => date.valueOf() > Date.now()} onChange={value => {
                    // UI Kit 范围值可能是 Date、Dayjs 或格式化字符串，轨迹接口统一收敛为 Dayjs。
                    if (value?.[0] && value[1]) updateActiveForm({ range: [dayjs(value[0]), dayjs(value[1])] });
                }} />
                <label><span>播放速度</span><Select value={activeForm.speed} onChange={speed => updateActiveForm({ speed })} options={[0.5, 1, 1.5, 2, 5, 10].map(value => ({ label: String(value), value }))} /></label>
            </div>
            <div className="truck-trajectory-actions">
                <Button aria-label="播放轨迹" disabled={!trackVisible} onClick={() => activeEntry && onPlay(activeEntry, activeForm.speed)}><i className="iconfont icon-Frame7" /></Button><span>播放</span>
                <Button aria-label="轨迹居中" disabled={!trackVisible} onClick={() => activeEntry && onCenter(activeEntry)}><i className="iconfont icon-_tuceng_1" /></Button><span>居中</span>
                <Button type="primary" onClick={() => validateAndQuery()}>确认</Button>
            </div>
        </div>
    </div>;
}

/** Vue 最小化后保留可拖动入口；React 先复刻其标题、展开和关闭行为。 */
export function TruckTrajectoryMini({ onOpen, onClose }: { onOpen: () => void; onClose: () => void }) {
    return <div className="truck-trajectory-mini"><span>查询轨迹</span><button aria-label="展开轨迹" type="button" onClick={onOpen}><i className="iconfont icon-Frame-110" /></button><button aria-label="关闭轨迹" type="button" onClick={onClose}><i className="iconfont icon-close" /></button></div>;
}
